import { useServicesContext } from "@modules/core/services/services.context"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { useInfiniteQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export const useProgramSasRequests = (programId: string, status: ProgramSubscriptionStatus, searchTerms?: string) => {
  const { programSasApi } = useServicesContext()

  const { data, isLoading, isFetching, refetch, fetchNextPage, isError, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["programSasRequests", programId, status, searchTerms],
      queryFn: async ({ pageParam }) => {
        return await programSasApi.getProgramSasPendingSubscriptions(
          programId,
          status,
          pageParam,
          20,
          searchTerms,
          undefined,
        )
      },
      staleTime: 1000 * 60 * 5,
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage.page >= lastPage.totalPages - 1) {
          return undefined
        }
        return lastPage.page + 1
      },
    })

  const programSubscribers = useMemo(
    () =>
      data?.pages.flatMap((page) =>
        page.content.map((programSubscriberDto) => ProgramSubscriberEntity.fromDto(programSubscriberDto)),
      ) ?? [],
    [data],
  )

  return {
    data: {
      ...data,
      content: programSubscribers,
      totalSize: data?.pages[0]?.totalSize ?? 0,
    },
    isLoading,
    isFetching,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}
